/**
 * WorkflowFunnel Component
 *
 * Visual funnel showing patient progression through workflow stages
 */

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface FunnelStage {
  stage: string;
  count: number;
  medianTimeHours: number;
  agingCount: number; // patients in stage >24h
}

interface WorkflowFunnelProps {
  stages: FunnelStage[];
  onStageClick?: (stage: string) => void;
  activeStage?: string | null;
}

const WorkflowFunnel: React.FC<WorkflowFunnelProps> = ({ stages, onStageClick, activeStage }) => {
  const formatTime = (hours: number): string => {
    if (hours < 24) {
      return `${Math.round(hours)}h`;
    }
    const days = Math.round(hours / 24);
    return `${days}d`;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
      {stages.map((stage, index) => {
        const isClickable = !!onStageClick;
        const isDischarged = stage.stage === 'DISCHARGED';
        const isActive = activeStage === stage.stage;

        // Don't show "At risk" for discharged patients, transition stage, or undefined agingCount
        // Transition is the final active stage - patients here are doing final logistics, not "at risk"
        const isTransition = stage.stage === 'TRANSITION';
        const showAtRisk = !isDischarged && !isTransition && stage.agingCount !== undefined;

        return (
          <React.Fragment key={stage.stage}>
            <Paper
              onClick={isClickable ? () => onStageClick(stage.stage) : undefined}
              sx={{
                flex: 1,
                p: 3,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                bgcolor: isActive ? 'primary.light' : 'background.paper',
                border: isActive ? '2px solid' : 'none',
                borderColor: isActive ? 'primary.main' : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 160,
                '&:hover': isClickable
                  ? {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    }
                  : {},
              }}
            >
              {/* Stage Name */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  display: 'block',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  textAlign: 'center',
                }}
              >
                {stage.stage}
              </Typography>

              {/* Count - centered and large */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1, color: 'text.primary' }}>
                  {stage.count}
                </Typography>
              </Box>

              {/* Metrics Section */}
              <Box sx={{ borderTop: '2px solid', borderColor: 'primary.main', pt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {stage.stage === 'DISCHARGED' ? 'Total time:' : 'Median time:'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {formatTime(stage.medianTimeHours)}
                  </Typography>
                </Box>
                {showAtRisk && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      At risk:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        color: stage.agingCount > 0 ? 'warning.main' : 'text.primary'
                      }}
                    >
                      {stage.agingCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {index < stages.length - 1 && (
              <ArrowForwardIcon sx={{ color: 'text.secondary', flexShrink: 0, mt: 5 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default WorkflowFunnel;
